import {
  addMonths,
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { Chart } from 'react-chartjs-2';
import _ from 'lodash';
import { isAdminAuthorized } from '../../app/userImpersonate';
import { toast } from 'react-toastify';
import { convertToDate, formatDate, fullDateFormat } from '../../utilities/common';

export const CALENDAR = 'Calendar Year';
export const FINANCIAL = 'Financial Year';
let i18nTranslation;

export const setDefaultTranslation = (t) => {
  i18nTranslation = t;
};

export const transBackend = (message) =>
  _.inRange(message?.length, 2, 255) ? i18nTranslation(message) : message;

export const transCsvHeader = (csvContent) => {
  const csvRows = csvContent?.split('\n');
  if (!csvRows?.length || !csvRows[0].includes(',')) {
    return false;
  }
  const csvHeader = csvRows[0].split(',')
    .map(title => transBackend(title?.trim()))
    .join(',');
  csvRows[0] = csvHeader;
  return csvRows.join('\n');
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// The API and DB keep energy usage in TJ, the UI shows GJ.
export const TJ_TO_GJ = 1000;
export const tjToGj = (num) => {
  if (['', undefined, null].includes(num) || isNaN(num)) return num;
  return Number(num) * TJ_TO_GJ;
};

export const formatNumber = (num, defaultValue = 0, isCost = false) => {
  if (['', undefined, null].includes(num) || isNaN(num)) {
    return defaultValue;
  }
  const value = Math.abs(Number(num));
  const getDecimal = () => {
    switch (true) {
      case value < 10:
        return 2;
      case value < 100 && !isCost:
        return 1;
      case value >= 10 && isCost:
      default:
        return 0;
    }
  };
  return formatDecimalNumber(num, getDecimal());
};

export const formatDecimalNumber = (num, decimal) => {
  const value = Math.abs(Number(num));
  const negative = Number(num) < 0 ? '-' : '';
  const formatted = value.toFixed(decimal).split('.');
  const first = formatted[0].replace(/(.)(?=(\d{3})+$)/g, '$1,');
  const second = formatted[1] && !/^[0]+$/.test(formatted[1]) ? `.${formatted[1]}` : '';
  const result = `${negative}${first}${second}`;
  if (result === '0' && value !== 0) {
    return '0.00';
  }
  return result;
}

export const formatUserName = (user) => {
  if (!user) return null;
  if (typeof user === 'string') return user;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.email || null;
};

export const parseFavouriteData = (search, dateFields = []) => {
  try {
    const params = new URLSearchParams(search);
    const frName = params.get('frName');
    const frUsrId = params.get('frUsrId');
    const filterQuery = params.get('filter');
    const decodedData = filterQuery ? atob(decodeURIComponent(filterQuery)) : '""';
    const filterData = JSON.parse(decodedData);
    dateFields.forEach(field => {
      if (filterData[field]) {
        filterData[field] = convertToDate(filterData[field]);
      }
    });
    return { frName, frUsrId, filterData };
  } catch (err) {
    console.log(err);
    return {};
  }
};

export const getCommonFilterPeriodRange = (data, deaultPeriodRange = null) => {
  const filterPeriod = data ? data.frRepFilterPeriodParam : null;
  switch (filterPeriod) {
    case '1':
      return {
        startDate: convertToDate(data.startDate),
        endDate: convertToDate(data.endDate),
      };
    case '2':
      const prevMonthFirstDate = startOfMonth(subMonths(new Date(data.baseDate), 1));
      const prevMonthLastDate = endOfMonth(prevMonthFirstDate);
      return {
        startDate: prevMonthFirstDate,
        endDate: prevMonthLastDate
      };
    case '3':
      return {
        startDate: subMonths(new Date(data.baseDate), 1),
        endDate: new Date(data.baseDate),
      };
    case '4':
      return {
        startDate: subMonths(new Date(data.baseDate), 3),
        endDate: new Date(data.baseDate)
      };
    case '5':
      return {
        startDate: subYears(new Date(data.baseDate), 1),
        endDate: new Date(data.baseDate)
      };
    default:
      if (deaultPeriodRange) {
        return {
          startDate: new Date(deaultPeriodRange.startDate),
          endDate: new Date(deaultPeriodRange.endDate),
        };
      }
      return {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(new Date().getFullYear(), 11, 31)
      };
  }
};

export const dateRangeFilterPeriodOptions = (filters, t, category, showDetail) => {
  const labelMap = {
    fixedRange : 'Fixed range',
    previousYear : 'Previous year',
  };
  if (showDetail) {
    switch (category) {
      case 'emissions-by-top-10-contributors':
      case 'emissions-by-top-10-entities':
      case 'emissions-by-top-10-eps':
      case 'emissions-top-10-type':
        labelMap.fixedRange = t(
          'Fixed range ( {{year}} - {{type}}s)',
          { year: filters.reportYear, type: t(filters.reportYearType) },
        );
        labelMap.previousYear = t('Previous year (Calendar Year)');
        break;
      case 'moving-average-report':
        labelMap.fixedRange = t('Fixed range ( - {{endDate}})', { endDate: formatDate(filters.endDate) });
        labelMap.previousYear = t('Previous year ({{year}})', { year: subYears(new Date(filters.endDate), 1).getFullYear() });
        break;
      default:
        labelMap.fixedRange = t('Fixed range ({{startDate}} - {{endDate}})', {
          startDate: formatDate(filters.startDate),
          endDate: formatDate(filters.endDate),
        });
        labelMap.previousYear = t('Previous year');
        break;
    }
  }
  return [
    {
      label: labelMap.fixedRange,
      value: '1'
    },
    { label: t('Previous calendar month'), value: '2' },
    { label: t('Previous month'), value: '3' },
    { label: t('Previous quarter'), value: '4' },
    { label: labelMap.previousYear, value: '5' }
  ];
};

export const getWeeklyDateOptions = (t) => ([
  { label: t('Monday'), value: 1 },
  { label: t('Tuesday'), value: 2 },
  { label: t('Wednesday'), value: 3 },
  { label: t('Thursday'), value: 4 },
  { label: t('Friday'), value: 5 },
  { label: t('Saturday'), value: 6 },
  { label: t('Sunday'), value: 7 },
]);

export const parseDateRangeValue = (favReports, frId, t, showDateRange = false) => {
  const favReport = favReports.find((item) => frId === item.frId);
  if (favReport) {
    const filterParams = JSON.parse(favReport.frRepParamJson);
    const dateRangeOptions = dateRangeFilterPeriodOptions(filterParams, t);
    const target = dateRangeOptions.find(item => item.value === filterParams.frRepFilterPeriodParam);
    if (target?.value === '1' && showDateRange === false) {
      return t('Fixed range');
    }
    return target?.label || '';
  }
  return '';
};

export const getSendingIntervalOptions = (t) => ([
  {
    label: t('Weekly'),
    value: "WEEKLY",
  },
  {
    label: t('Monthly'),
    value: "MONTHLY",
  },
  {
    label: t('Quarterly'),
    value: "QUARTERLY",
  },
  {
    label: t('Yearly'),
    value: "YEARLY",
  },
]);

export const customFilterPeriodOptions = (filters, t, category, showDetail) =>
  dateRangeFilterPeriodOptions(filters, t, 'emissions-top-10-type', showDetail)
    .filter(item => ['1', '5'].includes(item.value));

export const getIntervalTypeOptions = (t) => ([
  { label: t('Annual'), value: 'annual' },
  { label: t('Monthly'), value: 'monthly' },
  { label: t('Quarterly'), value: 'quarterly' }
]);

export const getEmissionTypeOptions = (t) => ([
  { label: t('CO2-e (t)'), value: '1' },
  { label: t('Usage'), value: '2' }
]);

export const reportUnitByTypeId = (type, t) => {
  if (type === 1) {
    return { unit: 'tCO2e', title: t('CO2-e (t)') };
  }
  return { unit: 'mixed', title: t('Usage') };
};

export const parseUnitJson = (jsonData, currentUnits) => {
  if (!jsonData) {
    return [];
  }
  const getListUnits = (text) => {
    const data = JSON.parse(text);
    if (data && data[0] && data[0].includes('["')) {
      return getListUnits(data[0]);
    }
    return data;
  };
  const listUnits = getListUnits(jsonData);
  if (!currentUnits) {
    return _.uniq(listUnits);
  }
  listUnits.forEach((current) => {
    if (!currentUnits.find((last) => last === current)) {
      currentUnits.push(current);
    }
  });
};

const displayUnitsMap = {
  CUBIC_METERS: 'm³', //²
  CUBIC_FEET: 'ft³',
  DOLLARS: '$',
  GIGA_JOULES: 'Gj',
  GALLONS: 'gal',
  KILOWATTHOURS: 'kWh',
  KILO_WATT_HOURS: 'kWh',
  TONNES_KILO_METERS: 'tkm',
  KILO_METERS: 'km',
  KILO_LITERS: 'kl',
  KILO_LITRES: 'kl',
  LITRES: 'l'
};

export const displayTargetUnit = (listUnits, defaultUnit, isMultiDepth) => {
  if (!listUnits || !listUnits.length || listUnits.every(item => _.isEmpty(item))) {
    return defaultUnit;
  }
  const displayUnitHandler = (units) =>
    units
      .filter((unit) => !!unit)
      .map((item) => {
        if (displayUnitsMap[item]) {
          return displayUnitsMap[item];
        }
        return item.replace(/_/g, ' ');
      });
  if (isMultiDepth) {
    const parsedUnits = listUnits.reduce((acc, units) => acc.concat(units), []);
    return displayUnitHandler(_.uniq(parsedUnits));
  }
  return displayUnitHandler(listUnits);
};

export const reportYearList = (financialYear) => {
  const normalizedFinancialYear = Number(financialYear) || null;
  const currentYear = Math.max(new Date().getFullYear(), normalizedFinancialYear || 0);
  const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);
  const yearList = range(currentYear, currentYear - 16, -1);
  const reportYears = [];
  yearList.map((item) => {
    return reportYears.push({
      label: item,
      value: item
    });
  });
  if (normalizedFinancialYear && !yearList.find(item => item === normalizedFinancialYear)) {
    reportYears.unshift({ label: normalizedFinancialYear, value: normalizedFinancialYear });
  }
  return reportYears;
};

let colorIndexes = {};
let colorTargetMaps = {};

const colorMaps = [
  "#f99e1b", "#0f8b43", "#41c5e7", "#d21f2a", "#ffce00",
  "#b084bb", "#b7cd15", "#ff7975", "#216aa8", "#8bb4ab",
  "#fd5802", "#47ac87", "#a8e0f9", "#b21f29", "#fae701",
  "#972068", "#d84871", "#657734", "#95e0d7", "#633f73",

  "#A2AF10", "#AC1C0F", "#882E9B", "#2D8F09", "#239382",
  "#CE4EB8", "#868BCE", "#0F64BF", "#D387C2", "#F3562E",

  "#7C6BAC", "#A57710", "#10AE65", "#BB60F4", "#1A83DB",
  "#C87A6D", "#B84E6C", "#419FC0", "#C1A057", "#7CB57D",

  "#ED4F8D", "#7147C0", "#667815", "#BA0C57", "#9E4B8E",
  "#126DF5", "#A36FC8", "#DA74F1", "#9D1F74", "#854EE8",
];
const randomColor = () => {
  const threshold = 50;
  const parts = Array.from(Array(3), (_) => Math.floor(Math.random() * 256)).sort((a, b) => a - b);
  if (parts[2] - parts[0] < threshold) {
    const exclude = Math.min(255, parts[0] + threshold) - Math.max(0, parts[2] - threshold);
    parts[1] = Math.floor(Math.random() * (256 - exclude));
    if (parts[1] >= parts[2] - threshold) parts[1] += exclude;
  }
  for (let i = parts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }
  return '#' + parts.map((p) => ('0' + p.toString(16)).slice(-2)).join('');
};
export const generateRandomColors = (isReset = false, dataType = 'none') => {
  if (isReset === true) {
    colorIndexes = {};
    colorTargetMaps = {};
    return;
  }
  const colorIndex = colorIndexes[dataType] || 0;
  colorIndexes[dataType] = colorIndex + 1;
  return colorMaps[colorIndex] || randomColor();
};

export const parseScopeNumber = (scopeName) => {
  const match = /Scope\s*(\d+)/i.exec(scopeName || '');
  return match ? Number(match[1]) : null;
};

export const orderByWeightThenLabel = (items, labelKey = 'label', weightKey = 'weight') => {
  const weighted = _.orderBy(items.filter((item) => item[weightKey]), weightKey, 'asc');
  const unweighted = _.orderBy(
    items.filter((item) => !item[weightKey]),
    (item) => String(item[labelKey] || '').toUpperCase(),
    'asc'
  );
  return [...weighted, ...unweighted];
};

// EPSs added straight to a Scope (instead of a Level 1 sub-category) are returned by the API
// under an "Uncategorized" category. A note is shown next to the chart whenever that happens.
export const hasUncategorizedCategory = (categoryNames = []) =>
  categoryNames.some((name) => /^uncategori[sz]ed$/i.test(String(name || '').trim()));

export const getColorForKey = (key, dataType = 'none') => {
  if (!colorTargetMaps[dataType]) {
    colorTargetMaps[dataType] = {};
  }
  colorTargetMaps[dataType][key] =
    colorTargetMaps[dataType][key] || generateRandomColors(false, dataType);
  return colorTargetMaps[dataType][key];
};

export const generateChartLegend = (chartRef, hiddenLegendIndex = null, setHiddenLegendsHandler = null) => {
  if (!chartRef.current) {
    return;
  }
  document.getElementById('js-legend').innerHTML = chartRef.current.chartInstance.generateLegend();
  const ci = chartRef.current.chartInstance;
  const datasets = ci.data.datasets;
  let legends = document.querySelectorAll('#js-legend ul li');
  [].map.call(legends, function (elem, elemIdx) {
    if (hiddenLegendIndex && elemIdx === hiddenLegendIndex) {
      elem.setAttribute("hidden", true);
    } else {
      if (setHiddenLegendsHandler) {
        elem.className = datasets[elemIdx].hidden ? 'strike' : '';
      }
      elem.addEventListener('click', function (e) {
        elem.classList.toggle('strike');
        const legendItem = ci.legend.legendItems.filter(
          (legendItem) => legendItem.text.replace(/\s+/g, ' ').trim() === elem.innerText
        );
        let index = legendItem[0].datasetIndex;

        let meta = ci.getDatasetMeta(index);
        meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null;
        if (setHiddenLegendsHandler) {
          setHiddenLegendsHandler(index)
        }
        ci.update();
      });
    }
  });
};

export const createInitChartPlugins = (plugins, options = {}) => {
  const defaultPlugins = [
    {
      beforeDraw: function (chart) {
        const ctx = chart.ctx;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
      },
      afterDatasetsDraw: (chartInstance) => {
        if (options?.displayChartAxesTotal && options.displayChartAxesTotal()) {
          const ctx = chartInstance.ctx;
          ctx.font = Chart.helpers.fontString(
            Chart.defaults.global.defaultFontSize,
            Chart.defaults.global.defaultFontStyle,
            Chart.defaults.global.defaultFontFamily
          );
          ctx.textAlign = 'left';
          ctx.textBaseline = 'bottom';
          ctx.fillStyle = 'black';
          ctx.fontWeight = 'bold';

          const totals = {};
          let atmost = 0;
          chartInstance.config.data.datasets.forEach((dataset, datasetIndex) => {
            if (chartInstance.isDatasetVisible(datasetIndex)) {
              atmost = datasetIndex;
              dataset.data.forEach((value, index) => {
                totals[index] = (totals[index] || 0) + value;
              });
            }
          });
          Object.keys(totals).map((key) => {
            const meta = chartInstance.controller.getDatasetMeta(atmost);
            const posX = meta.data[key]._model.x + 4;
            const posY = meta.data[key]._model.y;
            return ctx.fillText(formatNumber(totals[key]), posX, posY);
          });
        }
      }
    }
  ];
  return [
    ...defaultPlugins,
    ...plugins,
  ];
}

export const createInitChartConfig = (options) => {
  const commonConfigs = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    legend: {
      display: false
    },
    tooltips: {
      mode: 'nearest',
      intersect: true,
      bodySpacing: 3,
      backgroundColor: '#000',
      filter: function (tooltipItem, data) {
        let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
        return value !== 0;
      },
      callbacks: {
      }
    },
    hover: {
      animationDuration: 0
    }
  };
  return _.merge(commonConfigs, options);
};

export const wrapTooltipText = (text, maxCharsPerLine = 60) => {
  const words = String(text ?? '').trim().split(/\s+/);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (currentLine && nextLine.length > maxCharsPerLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = nextLine;
    }
  });

  if (currentLine) lines.push(currentLine);

  return lines;
};

const translateReportUnits = (t, unitOptions) => unitOptions.map(({ label, options }) => ({
  label: label === 'other' ? t('Other previous input') : t(label),
  options,
}));

export const getReportUnitOptions = (t, reportUnits) => {
  const unitOptions = [];
  _.forOwn(reportUnits, (unitItems, cat) => {
    const unitOption = { label: cat, options: [] };
    _.forOwn(unitItems, (unitItem) => {
      unitOption.options.push({ label: unitItem.name, value: unitItem.shortName });
    });
    unitOptions.push(unitOption);
  });
  return translateReportUnits(t, unitOptions);
};

export const groupReportUnitOptions = (t, reportUnits, flatUnits) => {
  const unitOptions = [];
  flatUnits.forEach(unit => {
    _.forOwn(reportUnits, (unitItems, cat) => {
      _.forOwn(unitItems, (unitItem) => {
        const includeUnits = [...unitItem.aliasNames, unitItem.name, unitItem.shortName]
        if (includeUnits.includes(unit)) {
          const idx = unitOptions.findIndex(opt => opt.label === cat);
          if (idx !== -1) {
            unitOptions[idx].options.push({ label: unitItem.name, value: unit });
          } else {
            unitOptions.push({ label: cat, options: [{ label: unitItem.name, value: unit }] });
          }
        }
      });
    });
  });
  return translateReportUnits(t, unitOptions);
};

export const getReportPeriodEnding = (currentReportPeriod, filterParams = null) => {  
  const reportYearType = filterParams?.reportYearType ?? currentReportPeriod?.reportYearType;
  return reportYearType === FINANCIAL ? currentReportPeriod.reportPeriodEnding : 12;
};

export const getCurrentReportPeriod = (usrOrgOptions) => {
  const { optReportPeriodType, optReportPeriodEnding } = usrOrgOptions || {};
  const now = new Date();
  let reportYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (optReportPeriodType && optReportPeriodEnding !== 12 && optReportPeriodEnding >= currentMonth) {
    reportYear -= 1;
  }
  return {
    reportYear,
    financialYear: optReportPeriodType && currentMonth > optReportPeriodEnding ? reportYear + 1: null,
    reportYearType: optReportPeriodType ? FINANCIAL : CALENDAR,
    reportPeriodEnding: optReportPeriodType ? optReportPeriodEnding : 12,
  };
};

export const getReportYearTypeOptions = (t, rangePeriod) => {
  const options = [
    { label: t('Calendar Year'), value: CALENDAR },
  ];
  if (rangePeriod?.isReportPeriod) {
    const { startDate, endDate } = rangePeriod;
    options.push({
      value: FINANCIAL,
      label: t(
        'Financial Year ({{startYear}}-{{endYear}})',
        { startYear: format(startDate, 'MMM'), endYear: format(endDate, 'MMM') },
      ),
    });
  }
  options.push({
    value: 'SETUP',
    label: t('Change Report Period'),
  });
  return options;
};

export const setupReportRangePeriod = (history) => {
  if (isAdminAuthorized()) {
    history.push(`/admin/settings/report-period`);
  } else {
    toast.error(transBackend('Please contact admin to access this feature.'));
  }
};

export const dataTableNumericSortHandler = (rowA, rowB, columnId) => {
  const a = parseFloat(rowA.values[columnId]);
  const b = parseFloat(rowB.values[columnId]);

  if (isNaN(a)) return 1;
  if (isNaN(b)) return -1;

  return a - b;
};

const getFiscalStartMonth = (reportPeriodEnding) => {
  if (!reportPeriodEnding || reportPeriodEnding === 12) { 
    return 0;
  }
  return reportPeriodEnding % 12;
};

export const getMonthAndQuarterNames = (t, reportPeriodEnding) => {
  const fiscalStartMonth = getFiscalStartMonth(reportPeriodEnding);
  const fiscalBaseDate = new Date(2000, fiscalStartMonth, 1);
  const calendarBaseDate = new Date(2000, 0, 1);
  const fm = 'MMM';

  const monthNames = [
    t('n/a'),
    ...Array.from({ length: 12 }, (_, i) =>
      format(addMonths(calendarBaseDate, i), fm)
    ),
  ];

  const quarterNames = [t('n/a')];

  for (let i = 0; i < 4; i++) {
    const qStart = addMonths(fiscalBaseDate, i * 3);
    const qEnd = addMonths(qStart, 2);

    quarterNames.push(
      `${format(qStart, fm)} - ${format(qEnd, fm)}`
    );
  }

  return { monthNames, quarterNames };
};

export const formatPeriodLabel = ({
  bucket,
  monthNames,
  quarterNames,
  reportPeriodEnding,
}) => {
  const isFiscal = getFiscalStartMonth(reportPeriodEnding) !== 0;

  const getQuarterDisplayYear = (year, quarter) => {
    if (!isFiscal) return year;

    const endMonth = getQuarterEndMonth(quarter, reportPeriodEnding);

    return endMonth <= reportPeriodEnding ? year : year - 1;
  };

  const getQuarterEndMonth = (quarter, endingMonth) => {
    const startMonth = (endingMonth % 12) + 1;
    const s0 = startMonth - 1;
    const end0 = (s0 + (quarter - 1) * 3 + 2) % 12;
    return end0 + 1;
  };

  if ('quarter' in bucket) {
    const year = getQuarterDisplayYear(bucket.year, bucket.quarter);
    return `${quarterNames[bucket.quarter]} ${year}`;
  }

  if ('month' in bucket) {
    return `${monthNames[bucket.month]} ${bucket.year}`;
  }

  return `${bucket.year}`;
};
// get start report period
export const getStartDatePeriod = (reportRangePeriod, reportYearType) => {
  if (reportYearType === CALENDAR) {
    return format(startOfYear(reportRangePeriod.startDate), fullDateFormat);
  }
  return format(reportRangePeriod.startDate, fullDateFormat);
};

export const getReportRangePeriod = (usrOrgOptions, reportYear = null, useCurrentYearBase = false) => {
  const { optReportPeriodType, optReportPeriodEnding } = usrOrgOptions || {};
  const isReportPeriod = !!optReportPeriodType;
  if (!isReportPeriod) {
    const baseDate = reportYear ? new Date(reportYear, 0, 1) : new Date();
    return {
      isReportPeriod,
      startDate: startOfYear(baseDate),
      endDate: endOfYear(baseDate),
    };
  }
  const endMonth = Number(optReportPeriodEnding);
  const resolvedYear = reportYear ?? getCurrentReportPeriod(usrOrgOptions).reportYear;
  if (useCurrentYearBase) {
    const startDate = new Date(resolvedYear, endMonth === 12 ? 0 : endMonth, 1);
    return {
      isReportPeriod,
      startDate,
      endDate: endOfMonth(addMonths(startDate, 11)),
    };
  }
  const endDate = new Date(resolvedYear, endMonth - 1, 1);
  return {
    isReportPeriod,
    startDate: subMonths(endDate, 11),
    endDate: endOfMonth(endDate),
  };
};

export const getListReportOptions = (t) => {
  const reportOptions = [
    {
      label: t('Carbon FootPrint By Entity'),
      value: 'CFBE',
      path: 'carbon-footprint-by-entity',
      tooltip: t('Business activity data converted into CO2e (and energy content when required), categorised by Entity. Shows absolute and proportional CO2e emissions of each business activity.'),
    },
    {
      label: t('Carbon FootPrint By Full Emission Profile'),
      value: 'CFBP',
      path: 'carbon-footprint-by-full-emission-profile',
      tooltip: t('Business activity data converted into CO2e, categorised by "scope" (as defined by GHG Protocol). Shows absolute and proportional CO2e emissions of each business activity.'),
    },
    {
      label: t('Carbon FootPrint By Scope'),
      value: 'CFBC',
      path: 'carbon-footprint-by-scope',
      tooltip: t('Business activity data converted into CO2e, categorised by "scope" (as defined by GHG Protocol). Shows absolute and proportional CO2e emissions of each business activity. Also has additional gas emissions (CH4, N2O, PFC, HFC, SF6).'),
    },
    {
      label: t('Data Gap'),
      value: 'DAGP',
      path: 'data-gap',
      tooltip: t('Monthly emissions data categorised by Entity. Shows data captured so far, highlighting "gaps" in reporting.'),
    },
    {
      label: t('Data Template IDs Report'),
      value: 'DTIR',
      path: 'data-template-ids',
      tooltip: t('A comprehensive list of all Entities, Assets, and Emission Source Profiles that have been set up for this account.'),
    },
    {
      label: t('Data Validation'),
      value: 'DAVN',
      path: 'data-validation',
      tooltip: t('Compare emissions data across different time periods. Set a tolerance for maximum allowable variation between datasets, and highlight outliers.'),
    },
    {
      label: t('Emissions By Asset'),
      value: 'EBAT',
      path: 'emissions-by-asset',
      tooltip: 'Business activity data converted into CO2e, categorised by the Assets that have been set up on this account.',
    },
    {
      label: t('Emissions By Entity Group'),
      value: 'EBEG',
      path: 'emissions-by-entity-group',
      tooltip: t("Total emissions categorised by each 'Entity Group' set up for this account."),
    },
    {
      label: t('GHG Snapshot Report'),
      value: 'GHGS',
      path: 'ghg-snapshot',
      tooltip: t("Simplified overview of the emissions footprint of this organisation, broken down by GHG Protocol Scope & the emissions 'Categories' that have been set up on this account."),
    },
    {
      label: t('Emissions By Entity Or Metric'),
      value: 'EBEM',
      path: 'emissions-by-entity-or-metric',
      tooltip: t('A high-level overview of emissions data, categorised by: Entity, Entity Business Number, Entity Group, Emissions Source, and Emission Source Group.'),
    },
    {
      label: t('Emissions By Top 10 Contributors'),
      value: 'T10C',
      path: 'emissions-by-top-10-contributors',
      tooltip: t("Business activity data converted into total emissions, organised by 'Provider', as specified during the data entry stage. Focuses only on the ten Providers with the highest overall carbon footprints."),
    },
    {
      label: t('Emissions By Top 10 Entities'),
      value: 'T10E',
      path: 'emissions-by-top-10-entities',
      tooltip: t("Annual summary of business activity data converted into total emissions. Narrowed down to the ten business activities that contribute the most to your organisation's overall carbon footprint."),
    },
    {
      label: t('Emissions by Top 10 Emission Profile Sources'),
      value: 'T10P',
      path: 'emissions-by-top-10-eps',
      tooltip: t("Annual summary of business activity data, converted into total emissions. Narrowed down to the ten entities that contribute the most to the organisation's carbon footprint."),
    },
    {
      label: t('Intensity Graph'),
      value: 'IYGH',
      path: 'intensity-graphs',
      tooltip: t('Emissions total, normalised against set Business Measures, as set up and reported on this account.'),
    },
    {
      label: t('Moving Average Report'),
      value: 'MART',
      path: 'moving-average-report',
      tooltip: t('Shows emissions of that month/quarter compared to average of the previous 12 months. Allows you to see if your org is trending up or down. Compare the average monthly/quarterly emissions of your organisation between one point in time and 12 months prior. Illustrates progress made by your emissions reduction strategies.'),
    },
    {
      label: t('Transaction Report'),
      value: 'TNRT',
      path: 'transaction',
      tooltip: t('A comprehensive report of every registered emissions source, asset, business activity data entry, and the resulting carbon emissions of each.'),
    },
  ];
  return _.sortBy(reportOptions, 'name');
};
